import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, LayoutGrid, Plus, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isApiError, errorMessage } from '@/api/client';
import {
  useConnections,
  useDashboard,
  useIntegrations,
  useRemoveWidget,
  useReorderWidgets,
  useSyncConnection,
} from '@/api/hooks';
import type { DashboardWidget, RangeKey } from '@/api/types';
import { useAuth } from '@/auth/context';
import { useToast } from '@/components/toast-context';
import { Button, ButtonLink, EmptyState, ErrorNote, LiveRegion, PageHeader, Segmented } from '@/components/ui';
import { useConnectFlow } from '@/features/connect/useConnectFlow';
import { formatPeriod } from '@/lib/format';
import { local } from '@/lib/storage';
import { AddKpisDialog } from './AddKpisDialog';
import { COMPARED_TO, RANGE_KEY, RANGES, readRange } from './range';
import { KpiCard, KpiCardSkeleton } from './KpiCard';

function SortableCard(props: {
  widget: DashboardWidget;
  comparedTo: string;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.widget.id });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'relative z-20' : undefined}
    >
      <KpiCard
        widget={props.widget}
        comparedTo={props.comparedTo}
        customising
        dragging={isDragging}
        handleProps={{ ...attributes, ...listeners }}
        onRemove={props.onRemove}
      />
    </li>
  );
}

function greeting(name: string | undefined) {
  const h = new Date().getHours();
  const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  return name ? `${part}, ${name.split(' ')[0]}` : part;
}

export function Dashboard() {
  const { user } = useAuth();
  const toast = useToast();
  const [range, setRangeState] = useState<RangeKey>(readRange);
  const dash = useDashboard(range);
  const connections = useConnections();
  const integrations = useIntegrations();
  const reorder = useReorderWidgets();
  const remove = useRemoveWidget();
  const sync = useSyncConnection();
  const { connect, busy: reconnecting } = useConnectFlow('/');
  const [customising, setCustomising] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [live, setLive] = useState('');
  const [reconnectId, setReconnectId] = useState<string | null>(null);

  // When a connection finishes syncing (e.g. right after connecting), its default KPIs and fresh
  // numbers land on the server: reload the dashboard once.
  const syncing = (connections.data ?? []).filter((c) => c.status === 'syncing' || c.status === 'pending').length;
  const wasSyncing = useRef(syncing);
  const { refetch } = dash;
  useEffect(() => {
    if (wasSyncing.current > syncing) void refetch();
    wasSyncing.current = syncing;
  }, [syncing, refetch]);

  const widgets = useMemo(() => dash.data?.widgets ?? [], [dash.data]);
  const ids = useMemo(() => widgets.map((w) => w.id), [widgets]);
  const comparedTo = COMPARED_TO[range];

  const setRange = (r: RangeKey) => {
    setRangeState(r);
    local.set(RANGE_KEY, r);
    setLive(`Showing ${RANGES.find((x) => x.value === r)?.label.toLowerCase()} compared with ${COMPARED_TO[r]}.`);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const nameOf = (id: string | number) => {
    const w = widgets.find((x) => x.id === id);
    return w ? `${w.integration_name} ${w.label}` : 'card';
  };
  const posOf = (id: string | number) => ids.indexOf(String(id)) + 1;
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${nameOf(active.id)}, position ${posOf(active.id)} of ${ids.length}.`,
    onDragOver: ({ active, over }) => (over ? `${nameOf(active.id)} moved to position ${posOf(over.id)} of ${ids.length}.` : `${nameOf(active.id)} is not over a position.`),
    onDragEnd: ({ active, over }) => (over ? `${nameOf(active.id)} dropped at position ${posOf(over.id)} of ${ids.length}.` : `${nameOf(active.id)} dropped.`),
    onDragCancel: ({ active }) => `Moving ${nameOf(active.id)} was cancelled.`,
  };

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const next = arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
    reorder.mutate(next, {
      onError: (err) => toast({ tone: 'error', title: 'Couldn’t save the new order', description: errorMessage(err) }),
    });
  }

  async function refreshAll() {
    const list = (connections.data ?? []).filter((c) => c.status === 'active' || c.status === 'error');
    if (!list.length) return;
    setRefreshing(true);
    setLive(`Refreshing ${list.length} app${list.length === 1 ? '' : 's'}…`);
    const results = await Promise.allSettled(list.map((c) => sync.mutateAsync(c.id)));
    const limited = results.filter((r) => r.status === 'rejected' && isApiError(r.reason, 429)).length;
    const failed = results.filter((r) => r.status === 'rejected' && !isApiError(r.reason, 429)).length;
    const ok = results.length - limited - failed;
    setRefreshing(false);
    const msg =
      ok > 0
        ? `Refreshing ${ok} app${ok === 1 ? '' : 's'}. Numbers update in a moment.`
        : limited > 0
          ? 'Everything was refreshed less than a minute ago.'
          : 'Couldn’t refresh right now.';
    setLive(msg);
    toast({ tone: failed && !ok ? 'error' : 'info', title: msg, description: limited && ok ? `${limited} were refreshed less than a minute ago.` : undefined });
    void dash.refetch();
  }

  async function reconnect(w: DashboardWidget) {
    const integration = integrations.data?.integrations.find((i) => i.id === w.integration_id);
    const conn = connections.data?.find((c) => c.id === w.connection_id);
    if (!integration) return;
    setReconnectId(w.id);
    await connect(integration, conn?.fields && Object.keys(conn.fields).length ? { fields: conn.fields } : undefined);
    setReconnectId(null);
  }

  const noConnections = connections.data && connections.data.length === 0;
  const period = dash.data ? `${formatPeriod(dash.data.period.start, dash.data.period.end)} vs ${formatPeriod(dash.data.previous_period.start, dash.data.previous_period.end)}` : '';

  return (
    <div>
      <PageHeader
        title={greeting(user?.name)}
        description={period ? <span className="tabular">{period}</span> : 'Your numbers at a glance.'}
        actions={
          <>
            <Segmented label="Date range" value={range} options={RANGES} onChange={setRange} />
            {!customising && widgets.length > 0 && (
              <Button onClick={() => void refreshAll()} loading={refreshing} icon={<RefreshCw className="size-4" aria-hidden />}>
                Refresh
              </Button>
            )}
            {widgets.length > 0 && (
              <Button
                variant={customising ? 'primary' : 'secondary'}
                aria-pressed={customising}
                onClick={() => setCustomising((c) => !c)}
                icon={customising ? <Check className="size-4" aria-hidden /> : <SlidersHorizontal className="size-4" aria-hidden />}
              >
                {customising ? 'Done' : 'Customise'}
              </Button>
            )}
          </>
        }
      />
      <LiveRegion message={live} />

      {customising && (
        <div className="mb-4 flex flex-col gap-3 rounded-card border border-dashed border-border bg-muted/60 p-4 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-ink">
            Drag the handle to reorder, or focus it and use the arrow keys (Space to pick up and drop). Remove a card with ×.
          </p>
          <Button variant="primary" size="sm" onClick={() => setAddOpen(true)} icon={<Plus className="size-4" aria-hidden />}>
            Add KPIs
          </Button>
        </div>
      )}

      {dash.isError && <ErrorNote title="Couldn’t load your dashboard" message={errorMessage(dash.error)} onRetry={() => void dash.refetch()} />}

      {dash.isPending && (
        <ul aria-label="Loading KPIs" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i}>
              <KpiCardSkeleton />
            </li>
          ))}
        </ul>
      )}

      {dash.data && widgets.length === 0 && (
        <EmptyState
          icon={<LayoutGrid className="size-5" aria-hidden />}
          title={noConnections ? 'Connect your first app' : 'Add your first KPIs'}
          actions={
            noConnections ? (
              <>
                <ButtonLink to="/onboarding" variant="primary">
                  Pick your apps
                </ButtonLink>
                <ButtonLink to="/integrations">Browse integrations</ButtonLink>
              </>
            ) : (
              <Button variant="primary" onClick={() => setAddOpen(true)} icon={<Plus className="size-4" aria-hidden />}>
                Add KPIs
              </Button>
            )
          }
        >
          {noConnections
            ? 'Connect Instagram, QuickBooks, Shopify, Stripe and more. Your headline numbers show up here, compared with the previous period.'
            : 'Your apps are connected. Pick the numbers you want to see on your dashboard.'}
        </EmptyState>
      )}

      {widgets.length > 0 &&
        (customising ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} accessibility={{ announcements }}>
            <SortableContext items={ids} strategy={rectSortingStrategy}>
              <ul aria-label="KPI cards, reorderable" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {widgets.map((w) => (
                  <SortableCard
                    key={w.id}
                    widget={w}
                    comparedTo={comparedTo}
                    onRemove={() =>
                      remove.mutate(w.id, {
                        onSuccess: () => toast({ title: `Removed ${w.label}`, description: `${w.integration_name} · ${w.connection_name}` }),
                        onError: (err) => toast({ tone: 'error', title: 'Couldn’t remove it', description: errorMessage(err) }),
                      })
                    }
                  />
                ))}
                <li>
                  <button
                    type="button"
                    onClick={() => setAddOpen(true)}
                    className="flex h-full min-h-[180px] w-full flex-col items-center justify-center gap-2 rounded-card border border-dashed border-border text-sm text-muted-ink hover:border-ink/40 hover:text-ink"
                  >
                    <Plus className="size-5" aria-hidden />
                    Add KPIs
                  </button>
                </li>
              </ul>
            </SortableContext>
          </DndContext>
        ) : (
          <ul aria-label="KPI cards" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {widgets.map((w) => (
              <li key={w.id}>
                <KpiCard
                  widget={w}
                  comparedTo={comparedTo}
                  onReconnect={() => void reconnect(w)}
                  reconnecting={reconnecting && reconnectId === w.id}
                />
              </li>
            ))}
          </ul>
        ))}

      {dash.data?.generated_at && widgets.length > 0 && (
        <p className="mt-6 text-xs text-muted-ink">
          Numbers are account totals from each app, in your time zone{user ? ` (${user.timezone})` : ''}.
        </p>
      )}

      <AddKpisDialog open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}
